import { PAC_INTRO_CALL_ANSWERS_PAYLOAD_ID } from '@bariendo/lib/constants';
import { BODY_HEIGHT_LOINC_CODING, BODY_WEIGHT_LOINC_CODING } from '@bariendo/lib/codings';
import {
  PAC_PREFILL_FIELD_IDS,
  PacAction,
  PacBeatContext,
  PacFactField,
  PacWidget,
} from '@bariendo/lib/patient-access-script';
import { NOT_READY_TO_COMMIT } from '@bariendo/lib/patient-access-warm-handoff-script';
import {
  buildPnConsultationSummary,
  PnConsultationAnswers,
  pnConsultationSaveBlocker,
  hasPnConsultationAnswer,
  PN_CALL_TRACK_ID,
  withoutInsuranceCardAnswers,
} from '@bariendo/lib/pn-consultation-note';
import {
  ageInYears,
  buildPnConsultationScript,
  describeEmployerBenefit,
  readVerificationTask,
  insuranceSectionDisabledReason,
  isViaFamily,
  PN_EMPLOYER_ID,
  insuranceTravelLabel as travelLabelForMarket,
  nearerTravelLocation,
  PN_INSURANCE_ELIGIBILITY_ID,
  PN_INSURANCE_PREFILLED_ID,
  PN_INSURANCE_SOURCE_ID,
  PN_INSURANCE_TRAVEL_ID,
  PN_INSURER_ID,
  PN_POLICYHOLDER_ID,
  policyholderRelationship,
  pregnancyQuestionApplies,
} from '@bariendo/lib/pn-consultation-script';
import {
  buildPnConsultationV2Script,
  cardIntakeSelection,
  PN_CARD_TRACK_LABELS,
  PN_CHERRY_APPLICATION_SENT_ID,
  switchTargetTrack,
  PN_EMPLOYER_LOCATION_ID,
  cardTrackForPatientTrack,
  CONFIRM_PROCEDURE_FIRST,
  isPnCardTrack,
  isPnCardV2Note,
  PN_CARD_TRACK_ID,
  PN_CARD_V2,
  PN_CARD_VERSION_ID,
  PnCardTrack,
  outcomeOptionsForCardTrack,
  recordedCardTrack,
  withCardTrack,
} from '@bariendo/lib/pn-consultation-v2-script';
import { answersThatApply } from '@bariendo/lib/beat-applicability';
import { observationHeightToCm, observationWeightToKg } from '@bariendo/lib/bmi';
import { answerString, answerStrings } from '@bariendo/lib/patient-access-note';
import { WEIGHT_RELATED_CONDITIONS_ID } from '@bariendo/lib/weight-related-conditions';
import { Anchor, Button, Group, Select, Text } from '@mantine/core';
import { createReference, getReferenceString } from '@medplum/core';
import { Communication, Observation, Patient, Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  TRACK_DESTINATIONS,
  TRACK_SWITCH_ACKNOWLEDGEMENT,
  trackTransition,
} from '@/components/SwitchTrackModal/trackTransition';
import { switchPatientTrack } from './trackSwitch';
import { QsaRowLink, TrackSwitchOnSave } from './PnConsultationV2Body';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  describeBmi,
  EnteredMeasurements,
  enteredFromObservations,
  enteredHeightCm,
  enteredWeightKg,
  formatWeightHeightBmi,
  heightObservation,
  weightObservation,
} from './bodyMeasurements';
import { WeightHeightInputs } from './WeightHeightInputs';
import { CherryAccountRow, WeightLossEstimateRow } from './computedWidgets';
import { PatientProfileFieldRow } from './PatientProfileFieldRow';
import { useCherryStatus } from '@/hooks/useCherryStatus';
import { describeCherryStatus } from '@/services/payments';
import { TimelineContext } from '@/contexts/timelineContexts';
import { PatientAccessCallCard } from './PatientAccessCallCard';
import { PnConsultationV2Body } from './PnConsultationV2Body';
import { Row } from './PatientAccessCallWidgets';
import { ProcedureDateGrid } from './ProcedureDateGrid';
import { summarizePnConsultationBeat } from './pnConsultationSummary';
import { useEligibilityPrefill } from './useEligibilityPrefill';
import { usePatientAccessActions } from './usePatientAccessActions';
import { openPaymentOrder } from './openPaymentOrder';
import { usePatientAccessCallDraft } from './usePatientAccessCallDraft';
import { usePatientProfileEditor } from './usePatientProfileEditor';
import { TRACK_NOT_RECORDED } from './preConsultationFacts';
import { usePreConsultationFacts } from './usePreConsultationFacts';
import { useProcedureBlockDates } from './useProcedureBlockDates';
import { useProcedureDateAnswer } from './useProcedureDateAnswer';
import { useProcedureLocations } from './useProcedureLocations';
import { calculateWeightLossEstimate } from './weightLossEstimate';
import { EmployerSearchWidget } from './EmployerSearchWidget';
import { EmployerVerificationTask } from './EmployerVerificationTask';
import { InsuranceVerificationTask } from './InsuranceVerificationTask';
import { InsuranceCardFieldRow } from './InsuranceCardFieldRow';
import { InsuranceCardDetails, writeInsuranceCoverage } from './insuranceVerification';
import { InsuranceCard, useInsuranceCard } from './useInsuranceCard';
import { CurrentUserContext } from '@/contexts/contexts';
import { InsurerSelectWidget } from './InsurerSelectWidget';
import { insurerListState, useCoverageArea } from './useCoverageArea';
import { useSignupInsurance } from './useInsurancePrefill';
import { usePatientTrack } from '@/hooks/usePatientTrack';
import { Track } from '@bariendo/lib/constants';
import { likelyInsuranceEligibility, pnCardV2InsuranceEligibility } from '@bariendo/lib/insurance-eligibility';
import { PolicyholderRow } from './PolicyholderRow';
import { PendingMark } from './PnConsultationGlanceRow';
import { readWeightRelatedConditions } from '@bariendo/lib/weight-related-conditions';
import { fillIntakeSelectionIfEmpty, writePatientEmployer } from './patientEmployer';
import { INTERESTED_SERVICE_EXTENSION, MARKET_LOCATION_EXTENSION } from '@bariendo/lib/extensions';
import { ELIGIBLE_LOCATIONS, employerMarkets, insuranceMarketForState } from '@bariendo/lib/eligible-locations';
import { alert } from '@/services/alert';
import { ORBIT_NEEDS_ACTION, ORBIT_SETTLED } from '@/theme/colors';
import { IconCheck, IconX } from '@tabler/icons-react';

/** The location select's id; its reference lives under `${LOCATION_ID}Reference`. */
const LOCATION_ID = 'loc';
const LOCATION_REFERENCE_ID = 'locReference';
/** The grid's answer id, distinct from the coarse-timing chips (`procdate`). */
const PROCEDURE_DATE_ID = 'procedure_date';
/** The pregnancy question's answer id. */
const PREGNANCY_ID = 'ql_preg';
/** Payment, a blood panel and a provider visit sit between the call and the procedure. */
const PROCEDURE_MIN_LEAD_DAYS = 14;
/** The shared prefill, plus the weight-related conditions only this card asks. */
const PREFILL_FIELDS = [...PAC_PREFILL_FIELD_IDS, WEIGHT_RELATED_CONDITIONS_ID];

/**
 * The answers without the agreed procedure date while the section it is picked
 * in is hidden (v2.0 on a coverage track). `answersThatApply` filters only
 * `values`; the date sits beside them.
 */
function withoutHiddenProcedureDate(answers: PnConsultationAnswers, applies: boolean): PnConsultationAnswers {
  if (applies || !answers.procedureDate) return answers;
  const { procedureDate: _hidden, ...rest } = answers;
  return rest;
}

/** The actions a viewer without the payment-order drawer cannot take (BAR-3070). */
const PAYMENT_ORDER_UNAVAILABLE: readonly PacAction[] = ['openPaymentOrder'];

interface PnConsultationCardProps {
  note: Communication;
  answers: PnConsultationAnswers;
  patient?: Patient;
  /**
   * A note already saved to the chart. Expanding it shows the worked script as a
   * record of the call; it is not an editor.
   */
  readOnly?: boolean;
  onSaved?: () => void;
  /**
   * The v2.0 card (BAR-2994, behind `enable-pn-card-v2`): one card for every
   * track, six sections of Question · Script · Answer. Off, the shipped card,
   * unchanged. Same note and answer ids, except the employer benefit's whose-job
   * and employment answers (BAR-2997), which only v2.0 writes: v2.0 reads a
   * shipped-card note, but a draft started on v2.0 and finished on the shipped
   * card (a per-user flag rollout, or a rollback) follows those v2.0 answers.
   */
  v2?: boolean;
  /** v2.0 only: ⋯ → Hide script, from the card header. */
  hideScript?: boolean;
  /**
   * v2.0 only: the note is saved to the chart and reopened. Its answers stay
   * editable (BAR-2996), but nothing writes into it on its own: no prefill, no
   * recorded track, no recomputed eligibility until the navigator changes
   * something. It shows the track it recorded, and has no Save to chart.
   */
  charted?: boolean;
}

/**
 * The PN consultation: the free 25-minute call a patient self-books from the
 * eligibility flow. Owns what is specific to that script — BMI and the
 * weight-loss estimate from the patient's measurements, the pregnancy gate, the
 * live location select, the physicians and procedure dates read off that site's
 * board, and the financing pivots — and renders the shared card spine.
 *
 * Saving is gated: a note charts only once it carries an outcome, because the
 * outcome is what the downstream events key off.
 */
export function PnConsultationCard({
  note,
  answers: storedAnswers,
  patient,
  readOnly = false,
  onSaved,
  v2 = false,
  hideScript = false,
  charted: chartedProp = false,
}: PnConsultationCardProps) {
  const charted = v2 && chartedProp;
  const medplum = useMedplum();
  // A note drafted before the member ID and group number moved to the
  // Coverage may carry them. Dropped before anything holds, shows or saves the
  // answers: the note is read by roles that cannot read Coverage.
  const initialAnswers = useMemo(() => withoutInsuranceCardAnswers(storedAnswers), [storedAnswers]);

  // The pregnancy question is built in or left out per patient — never rendered
  // as a row to skip. Memoised so the beat array keeps a stable identity.
  const askPregnancy = pregnancyQuestionApplies(patient);
  // The key facts, the identity rows and the insurance card rows show only
  // while the note is being written; a saved note is a record of the call, and
  // values read from today's chart would misstate it.
  const v1Script = useMemo(
    () =>
      buildPnConsultationScript({
        askPregnancy,
        keyFacts: !readOnly,
        confirmDetails: !readOnly,
        insuranceCard: !readOnly,
      }),
    [askPregnancy, readOnly]
  );

  // What the beat and group rules read besides the answers (BAR-2919): the
  // track (the self-pay line) and where insurance is offered (the insurance
  // section's state rule). Not looked up on a saved note: its rules skip them.
  const { track, loading: trackLoading } = usePatientTrack(readOnly ? undefined : patient?.id);
  const coverageArea = useCoverageArea(patient, !readOnly);
  const context = useMemo(
    (): PacBeatContext => (readOnly ? {} : { track, trackLoaded: !trackLoading, coverageArea }),
    [readOnly, track, trackLoading, coverageArea]
  );
  // What decides which answers apply: the same, except that a lookup still
  // loading skips the state rule, as a failed one does. The section grays
  // while it loads, but nothing has said insurance isn't offered, so a save in
  // that window keeps the insurance answers.
  const applicabilityContext = useMemo(
    (): PacBeatContext =>
      context.coverageArea?.status === 'loading' ? { ...context, coverageArea: undefined } : context,
    [context]
  );

  // A section that stopped applying keeps its answers on the draft but leaves
  // the summary and every action: both read `answersThatApply`. Through a ref,
  // so the summary builder keeps one identity while the patient object changes
  // on every live update.
  const applicability = useRef({
    script: v1Script,
    patient,
    context: applicabilityContext,
    procedureDateApplies: true,
  });
  const buildSummary = useCallback((next: PnConsultationAnswers) => {
    const {
      script: current,
      patient: chartPatient,
      context: currentContext,
      procedureDateApplies,
    } = applicability.current;
    const applicable = withoutHiddenProcedureDate(
      answersThatApply(current, next, chartPatient, currentContext),
      procedureDateApplies
    );
    // An identity edit on this card can close the pregnancy gate after the
    // question was answered. The gate leaves the question out of the script
    // rather than graying it, so it is dropped here: kept in the draft, left
    // out of the summary while the gate is closed. Not before the Patient
    // loads: no Patient is not a closed gate.
    if (!chartPatient || pregnancyQuestionApplies(chartPatient) || applicable.values[PREGNANCY_ID] === undefined) {
      return buildPnConsultationSummary(applicable);
    }
    const { [PREGNANCY_ID]: _hidden, ...values } = applicable.values;
    return buildPnConsultationSummary({ ...applicable, values });
  }, []);

  /** Latest answers, for the actions that run once the note is charted. */
  const answersRef = useRef(initialAnswers);

  /** The insurance card rows' state, for the actions that run once the note is charted. */
  const insuranceCardRef = useRef<InsuranceCard | undefined>(undefined);
  /** What the rows held when Save to chart was clicked, before the save can turn the note read-only. */
  const heldAtSaveRef = useRef<InsuranceCardDetails>({});
  // `useContext`, not `useCurrentUser`: no provider means no role. The same
  // gate as the verification button, a subset of the bot's `canEditInsuranceCard`
  // (which adds Finance, who never sees this card); the bot's check is the boundary.
  const mayEditInsuranceCard = useContext(CurrentUserContext)?.currentUserPermissions.canAccessPayments ?? false;

  /** v2.0: the track whose Next step fills Intake and billing on save. Unset on the shipped card. */
  const intakeTrackRef = useRef<PnCardTrack | undefined>(undefined);
  /**
   * The actions a charted note takes: the employer the call recorded goes to
   * the Patient, and on v2.0 the procedure and location fill an empty Intake
   * and billing row (BAR-3000). The coverage buttons act at once instead, from
   * the same applicable answers (BAR-2921).
   *
   * Only the patient's own employer: a family member's is recorded under its
   * own id, because the extension is the patient's, and Customer.io and the
   * next call's prefill read it as such.
   *
   * The Patient writes run one after another, never together: each reads the
   * Patient and writes its extensions back, so two at once would keep only
   * the last one's.
   */
  const handleSaved = useCallback(() => {
    const applicable = answersThatApply(
      applicability.current.script,
      answersRef.current,
      applicability.current.patient,
      applicability.current.context
    );
    // Not a family member's employer: v2.0 asks it in the same row (BAR-2997).
    const employer = isViaFamily(applicable) ? undefined : answerString(applicable.values[PN_EMPLOYER_ID])?.trim();
    const patientId = applicability.current.patient?.id;
    // v2.0 (BAR-3000): the procedure and the Next step's location fill an
    // empty Intake and billing row on save; one set by hand is left alone.
    const intakeTrack = intakeTrackRef.current;
    if (patientId) {
      const { procedure, market } = intakeTrack ? cardIntakeSelection(applicable, intakeTrack) : {};
      void (async () => {
        if (employer) {
          await writePatientEmployer(medplum, patientId, employer).catch(() =>
            alert.error('The note is saved, but the employer could not be saved to the profile.')
          );
        }
        const fills: [string, string | undefined][] = [
          [INTERESTED_SERVICE_EXTENSION, procedure],
          [MARKET_LOCATION_EXTENSION, market],
        ];
        let failed = false;
        for (const [url, value] of fills) {
          if (!value) continue;
          await fillIntakeSelectionIfEmpty(medplum, patientId, url, value).catch(() => {
            failed = true;
          });
        }
        if (failed) alert.error('The note is saved, but Intake and billing could not be updated from it.');
      })();
    }
    // Card details still held when Save was clicked go nowhere once the card
    // collapses: saved to the insurance card if there is one now, otherwise
    // said rather than dropped quietly. Never the values.
    const held = heldAtSaveRef.current;
    heldAtSaveRef.current = {};
    void insuranceCardRef.current?.settleOnNoteSave(held).then((outcome) => {
      const reason = {
        nothing: undefined,
        written: undefined,
        none: 'the patient has no insurance card on file until they move to the Insurance track',
        unreadable: 'the insurance card could not be read',
        failed: 'saving them to the insurance card failed',
      }[outcome];
      if (reason) {
        alert.warning({
          title: 'Insurance card details not saved',
          message: `The note is saved, but the insurance card details typed on it were not kept: ${reason}.`,
          options: { autoClose: false },
        });
      }
    });
    onSaved?.();
  }, [medplum, onSaved]);

  const { answers, updateAnswers, setValue, saveToChart, saveNow, isSaving, saveError } =
    usePatientAccessCallDraft<PnConsultationAnswers>({
      note,
      initialAnswers,
      answersPayloadId: PAC_INTRO_CALL_ANSWERS_PAYLOAD_ID,
      buildSummary,
      onSaved: handleSaved,
    });
  answersRef.current = answers;
  /** The answers as opened: on a charted note, anything else is the navigator's edit. */
  const openedWith = useRef(answers);
  const editedSinceOpen = answers !== openedWith.current;

  // v2.0 marks the note with the navigator's first answer, so it stays on v2.0
  // for a user without the flag (BAR-2994). A charted note reopened is marked
  // only once edited: nothing writes into it on its own (BAR-2996).
  const markV2 =
    v2 && !readOnly && (!charted || editedSinceOpen) && !isPnCardV2Note(answers) && hasPnConsultationAnswer(answers);
  useEffect(() => {
    if (markV2)
      updateAnswers((current) => ({ ...current, values: { ...current.values, [PN_CARD_VERSION_ID]: PN_CARD_V2 } }));
  }, [markV2, updateAnswers]);

  // v2.0: the track the card shows. The chip the navigator picked, once they
  // pick one; until then the patient's own track, read live. A saved note
  // shows the chip it recorded, else the track the card last read while it
  // was worked (BAR-2925), else self-pay; its chips still switch what it
  // shows, without writing, so answers a shipped-card note holds for another
  // path stay reachable. Undefined only while a draft's track is being read.
  const [savedNoteTrack, setSavedNoteTrack] = useState<PnCardTrack | undefined>(undefined);
  const storedCardTrack = isPnCardTrack(answers.values[PN_CARD_TRACK_ID])
    ? answers.values[PN_CARD_TRACK_ID]
    : undefined;
  const recordedTrack = answerString(answers.values[PN_CALL_TRACK_ID]) as Track | undefined;
  const cardTrack: PnCardTrack | undefined = !v2
    ? undefined
    : readOnly || charted
      ? ((readOnly ? savedNoteTrack : undefined) ?? recordedCardTrack(answers))
      : (storedCardTrack ??
        (trackLoading ? recordedTrack && cardTrackForPatientTrack(recordedTrack) : cardTrackForPatientTrack(track)));
  // Why insurance doesn't apply. Read with the shipped card's lookup-loading
  // allowance: while the state loads the rows show, and a save keeps their answers.
  // Before the procedure is confirmed (section 3) insurance is waiting on it, not ruled out.
  const insuranceReason = v2 ? insuranceSectionDisabledReason(answers, patient, applicabilityContext) : undefined;
  const insuranceUnavailable =
    insuranceReason && answerString(answers.values.procedure) === undefined ? CONFIRM_PROCEDURE_FIRST : insuranceReason;
  const v2Track = cardTrack ?? 'self-pay';
  const v2Script = useMemo(
    () =>
      v2
        ? buildPnConsultationV2Script({
            track: v2Track,
            askPregnancy,
            // A saved note shows every row it recorded, as the shipped card does.
            insuranceUnavailable: readOnly ? undefined : insuranceUnavailable,
            confirmDetails: !readOnly,
            // Not on a charted note: with no Save to chart, what they hold for a
            // patient with no insurance card would have nowhere to go.
            insuranceCard: !readOnly && !charted,
          })
        : undefined,
    [v2, v2Track, askPregnancy, insuranceUnavailable, readOnly, charted]
  );
  // What decides which answers apply. On a saved note, the insurance rules
  // still apply as they do on the shipped card's.
  const v2ApplicabilityScript = useMemo(
    () =>
      v2 && readOnly
        ? buildPnConsultationV2Script({
            track: v2Track,
            askPregnancy,
            insuranceUnavailable,
            confirmDetails: false,
            insuranceCard: false,
          })
        : v2Script,
    [v2, readOnly, v2Track, askPregnancy, insuranceUnavailable, v2Script]
  );
  /** What decides which answers apply, on either card. */
  const script = v2ApplicabilityScript ?? v1Script;
  // The procedure date is kept beside the answers, not among them, so the
  // track rule that hides v2.0's self-pay Next step is applied to it here.
  const procedureDateApplies = !v2 || v2Track === 'self-pay';
  applicability.current = { script, patient, context: applicabilityContext, procedureDateApplies };

  const handleTrackChange = useCallback(
    (next: PnCardTrack) => {
      if (readOnly) setSavedNoteTrack(next);
      else updateAnswers((current) => withCardTrack(current, next));
    },
    [readOnly, updateAnswers]
  );

  // R10 holds however the track changes: a chip, or the patient's track moving
  // underneath a draft that follows it. An outcome the shown track doesn't
  // offer is cleared rather than kept as an extra option.
  // Only on a track actually known: a chip, or a live track that was read. An
  // unread or failed read falls back to self-pay for display, and must not
  // cost the draft a coverage outcome.
  const recordedOutcome = answerString(answers.values.close);
  const trackKnown = storedCardTrack !== undefined || (Boolean(patient?.id) && !trackLoading && track !== undefined);
  const outcomeOffTrack =
    v2 &&
    !readOnly &&
    // A charted note keeps its outcome unless its track moved since it was
    // opened: an older note's outcome stands until a switch makes it off-track.
    (!charted || cardTrack !== recordedCardTrack(openedWith.current)) &&
    trackKnown &&
    cardTrack !== undefined &&
    recordedOutcome !== undefined &&
    !(outcomeOptionsForCardTrack(cardTrack) as readonly string[]).includes(recordedOutcome);
  useEffect(() => {
    if (!outcomeOffTrack) return;
    updateAnswers((current) => ({ ...current, values: { ...current.values, close: undefined } }));
  }, [outcomeOffTrack, updateAnswers]);

  // A saved note shows what its summary records: a section that had stopped
  // applying when it was charted reads as empty, not as answered. The payload
  // itself keeps every answer.
  const applicableAnswers = useMemo(
    () =>
      withoutHiddenProcedureDate(
        answersThatApply(script, answers, patient, applicabilityContext),
        procedureDateApplies
      ),
    [script, answers, patient, applicabilityContext, procedureDateApplies]
  );
  const shownAnswers = readOnly ? applicableAnswers : answers;
  // Only a note being written fills Intake and billing on save (BAR-3000).
  intakeTrackRef.current = v2 && !readOnly ? cardTrack : undefined;

  // v2.0's employer Location (BAR-3000): the confirmed administrator's
  // centres. A location the current partner doesn't offer is cleared, so a
  // new employer never keeps the last one's site.
  const employerBenefit = describeEmployerBenefit(applicableAnswers);
  const employerAdministrator = employerBenefit?.covered ? employerBenefit.administrator : undefined;

  // v2.0 (BAR-2995): the chip becomes the patient's track when the card is
  // saved, not on each click, through the same switch and acknowledgement as
  // the Switch track dialog, and only with the payments role (decided
  // 2026-10-19). Without it, or before an employer path has a partner to
  // switch to, saving leaves the track and the card says so.
  const switchTarget = v2 && cardTrack ? switchTargetTrack(cardTrack, employerAdministrator) : undefined;
  const switchOnSave: TrackSwitchOnSave | undefined = useMemo(() => {
    if (!v2 || readOnly || charted || !cardTrack) return undefined;
    // Not decided on a track still being read: saving waits rather than charting without the switch.
    // Only when the chip was clicked: otherwise the card follows the track and nothing would switch.
    if (trackLoading) return storedCardTrack ? { waiting: 'Reading the patient’s track…' } : undefined;
    if (!switchTarget) {
      // Already on an employer track (a partner, or an unverified employer): nothing to say.
      return cardTrack === 'employer' &&
        track !== Track.CARRUM &&
        track !== Track.TRANSCARENT &&
        track !== Track.EMPLOYER
        ? { blocked: 'Saving won’t move the patient to an employer track until a partner benefit is confirmed.' }
        : undefined;
    }
    if (switchTarget === track || (switchTarget === Track.D2C && track === undefined)) return undefined;
    const label = TRACK_DESTINATIONS.find((destination) => destination.track === switchTarget)?.label ?? switchTarget;
    if (!mayEditInsuranceCard) {
      return { blocked: `Saving won’t move the patient to ${label}: that needs the payments role.` };
    }
    return {
      to: switchTarget,
      label: `Saving moves the patient to ${label}.`,
      acknowledgement: trackTransition(track, switchTarget).needsAcknowledgement
        ? TRACK_SWITCH_ACKNOWLEDGEMENT
        : undefined,
    };
  }, [v2, readOnly, charted, trackLoading, storedCardTrack, cardTrack, switchTarget, track, mayEditInsuranceCard]);

  /** One save at a time: a second click while the switch runs does nothing. */
  const [switchingTrack, setSwitchingTrack] = useState(false);
  const switchingTrackRef = useRef(false);
  /** Save to chart on v2.0: the track switch first, so a refused switch leaves the note open to retry. */
  const saveV2 = useCallback(
    async (acknowledged: boolean) => {
      if (switchingTrackRef.current) return;
      // Held through the Coverage write and the save itself: the track re-read
      // can land first, and the chips and Save stay put until the note is charted.
      switchingTrackRef.current = true;
      setSwitchingTrack(true);
      try {
        // What the card rows hold goes to the note-save settle, unless the move
        // to Insurance below already wrote them (or tried to, and said so).
        let held: InsuranceCardDetails = insuranceCardRef.current?.pending ?? {};
        if (switchOnSave && 'to' in switchOnSave && patient) {
          try {
            // Who switched, for the Provenance: read at the moment of the switch.
            const profile = medplum.getProfile() as Practitioner | undefined;
            await switchPatientTrack(
              medplum,
              patient,
              profile,
              track,
              switchOnSave.to,
              acknowledged,
              'PnConsultationCard'
            );
          } catch (error) {
            alert.error(
              `The patient’s track could not be switched, so the note isn’t saved yet: ${
                error instanceof Error ? error.message : 'Unknown error'
              }`
            );
            return;
          }
          // A move to Insurance writes what the call recorded to the Coverage the
          // switch creates or adopts, as the insurance button does: the insurer and
          // source, with the card rows' member ID and group, so a member ID never
          // lands under the insurer signup recorded. The Intake and billing
          // Location is filled on save only when empty (BAR-3000).
          if (switchOnSave.to === Track.INSURANCE) {
            const applicable = answersThatApply(
              applicability.current.script,
              answersRef.current,
              applicability.current.patient,
              applicability.current.context
            );
            const write = (rows: InsuranceCardDetails) => writeInsuranceCoverage(medplum, patient, applicable, rows);
            held = {};
            await (insuranceCardRef.current ? insuranceCardRef.current.writeHeld(write) : write({})).catch(() =>
              alert.error(
                'The patient is on the Insurance track, but the insurance card could not be updated from the call.'
              )
            );
          }
          // The note records the track it moved the patient to, in the same save,
          // as a coverage button's switch does: the re-read lands after the note is charted.
          const movedTo = switchOnSave.to;
          updateAnswers((current) => ({ ...current, values: { ...current.values, [PN_CALL_TRACK_ID]: movedTo } }));
        }
        heldAtSaveRef.current = held;
        await saveToChart();
      } finally {
        switchingTrackRef.current = false;
        setSwitchingTrack(false);
      }
    },
    [switchOnSave, patient, medplum, track, saveToChart, updateAnswers]
  );
  const employerLocation = answerString(answers.values[PN_EMPLOYER_LOCATION_ID]);
  // Cleared only on a verdict: a confirmed partner that doesn't offer the
  // site, or no benefit at all. An unchecked or pending verdict leaves it.
  const employerLocationOffered =
    !employerLocation ||
    employerBenefit === undefined ||
    (employerBenefit.covered &&
      (employerMarkets(employerAdministrator) as readonly string[]).includes(employerLocation));
  useEffect(() => {
    // Only while the employer path shows and its partner is read: another
    // track's view, or a track still loading, leaves the answer as it is.
    if (!v2 || readOnly || employerLocationOffered || cardTrack !== 'employer' || trackLoading) return;
    if (charted && !editedSinceOpen) return;
    updateAnswers((current) => ({ ...current, values: { ...current.values, [PN_EMPLOYER_LOCATION_ID]: undefined } }));
  }, [v2, readOnly, charted, editedSinceOpen, employerLocationOffered, cardTrack, trackLoading, updateAnswers]);

  const [measurements, setMeasurements] = useState<{ weight?: Observation; height?: Observation }>({});
  /** Whether the measurement read has settled, so the pinned BMI fact can tell "loading" from "not on file". */
  const [measurementsLoaded, setMeasurementsLoaded] = useState(false);
  /** The site's board is only read while the financing beat is on screen. */
  const [openBeat, setOpenBeat] = useState<string | null>(null);
  /** v2.0's sections open independently. */
  const [openSections, setOpenSections] = useState<ReadonlySet<string>>(() => new Set());
  // Where the shipped card's insurance and self-pay beats live on v2.0: the
  // Money check and Next step, on that track.
  const insuranceOnScreen = v2 ? cardTrack === 'insurance' && openSections.has('check') : openBeat === 'insurance';
  const selfPayNextOnScreen = v2 ? cardTrack === 'self-pay' && openSections.has('next') : openBeat === 'next';

  // The member ID and group number rows: read from and saved to the insurance
  // Coverage, never the answers. Read once the insurance beat is opened, and
  // again when the track changes; never for a saved note, which leaves them out.
  const insuranceCard = useInsuranceCard(patient, note.id, {
    active: !readOnly && !charted,
    read: !readOnly && !charted && insuranceOnScreen,
    rereadKey: track,
  });
  insuranceCardRef.current = insuranceCard;

  const locations = useProcedureLocations();
  // The select stores both name and reference, but the eligibility prefill fills
  // only the name, so a stored name with no reference is resolved the same way
  // re-picking it would. Every registry market is also a procedure Location.
  const locationName = typeof answers.values[LOCATION_ID] === 'string' ? answers.values[LOCATION_ID] : undefined;
  const locationReference =
    typeof answers.values[LOCATION_REFERENCE_ID] === 'string'
      ? answers.values[LOCATION_REFERENCE_ID]
      : locations.options.find((option) => option.name === locationName)?.reference;
  // Scoped to the chosen site, and not read at all until one is chosen: unscoped,
  // the bot returns the next blocks at every clinic.
  const dates = useProcedureBlockDates(
    selfPayNextOnScreen && Boolean(locationReference),
    locationReference ? [locationReference] : undefined,
    { minLeadDays: PROCEDURE_MIN_LEAD_DAYS }
  );

  // Same beat as the board read, and gated for the same reason: this one reaches
  // Cherry, which rate-limits the lookup (BAR-2604).
  //
  // `useContext`, not `useTimelineContext`: that one throws without a provider,
  // and a supplementary status row must not decide whether this card renders.
  const onCherryStatus = useContext(TimelineContext)?.onCherryStatus;
  const { status: cherryStatus, loading: cherryChecking } = useCherryStatus(selfPayNextOnScreen, onCherryStatus);

  // The track as last read while the note is worked, for the collapsed line
  // (BAR-2925): re-read after a coverage button's switch. Not written to an
  // untouched draft, and never guessed: an unread track records nothing.
  const worked = hasPnConsultationAnswer(answers);
  useEffect(() => {
    if (readOnly || charted || trackLoading || !track || !worked) return;
    updateAnswers((current) =>
      current.values[PN_CALL_TRACK_ID] === track
        ? current
        : { ...current, values: { ...current.values, [PN_CALL_TRACK_ID]: track } }
    );
  }, [readOnly, charted, trackLoading, track, worked, updateAnswers]);

  // A coverage button's done step, saved at once so a retry can't repeat it.
  // A switch also moves the recorded track (BAR-2925) in the same write, so
  // the collapsed line names where the patient went even if the note is saved
  // before the track is read again.
  const recordCoverageStep = useCallback(
    (id: string, value: string) => {
      const record = readVerificationTask(value);
      updateAnswers((current) => ({
        ...current,
        values: {
          ...current.values,
          [id]: value,
          ...(record?.outcome === 'switched' && record.track ? { [PN_CALL_TRACK_ID]: record.track } : {}),
        },
      }));
      void saveNow();
    },
    [updateAnswers, saveNow]
  );

  const { handleAction, pendingAction } = usePatientAccessActions(patient);
  // Self-pay's Create payment order (BAR-3070): the warm hand-off's action, so
  // the same drawer. Read like `onCherryStatus`, without requiring a provider.
  // Absent for a viewer who cannot take payments, so the link shows disabled.
  const onOpenPaymentOrder = useContext(TimelineContext)?.onOpenPaymentOrder;
  const unavailableActions = onOpenPaymentOrder ? undefined : PAYMENT_ORDER_UNAVAILABLE;
  const handleScriptAction = useCallback(
    (action: PacAction) => {
      if (action === 'openPaymentOrder') {
        openPaymentOrder(onOpenPaymentOrder);
        return;
      }
      void handleAction(action);
    },
    [handleAction, onOpenPaymentOrder]
  );
  const { handleSelectDate, handleDecline } = useProcedureDateAnswer(updateAnswers, PROCEDURE_DATE_ID);
  // Never into a saved note: the prefill autosaves, and a charted call must stay as it was charted.
  useEligibilityPrefill(patient, updateAnswers, { fields: PREFILL_FIELDS, enabled: !readOnly && !charted });
  // Date of birth and sex assigned at birth write the Patient; undefined keeps them read-only.
  const saveProfileField = usePatientProfileEditor(patient, readOnly);

  // Latest weight and height back BMI and the weight-loss estimate. One search
  // per code rather than one window over both: a patient with regular weigh-ins
  // has dozens of weights newer than their one height, and any shared window
  // would drop the height. A failure leaves the fields prompting the navigator
  // rather than breaking the card.
  useEffect(() => {
    if (!patient?.id) return;
    let cancelled = false;
    const subject = getReferenceString(patient);

    const latest = (coding: typeof BODY_WEIGHT_LOINC_CODING) =>
      medplum.searchResources('Observation', {
        subject,
        code: `${coding.system}|${coding.code}`,
        _sort: '-date',
        _count: 1,
      });

    Promise.all([latest(BODY_WEIGHT_LOINC_CODING), latest(BODY_HEIGHT_LOINC_CODING)])
      .then(([weights, heights]) => {
        if (!cancelled) setMeasurements({ weight: weights[0], height: heights[0] });
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setMeasurementsLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [medplum, patient]);

  // v2.0 (BAR-2999): weight and height as the navigator types them, over the
  // latest Observations. Only boxes typed in since the last write are held
  // here; the BMI, the eligibility line and the weight-loss estimate follow
  // them on every keystroke. A box holding nothing usable gives no BMI.
  const [entered, setEntered] = useState<Partial<EnteredMeasurements>>({});
  const [savingMeasurement, setSavingMeasurement] = useState(false);
  const shownEntered = useMemo(
    () => ({ ...enteredFromObservations(measurements.weight, measurements.height), ...entered }),
    [measurements, entered]
  );
  const liveWeight = useMemo(() => {
    if (!v2 || entered.lb === undefined) return measurements.weight;
    const kg = enteredWeightKg(entered.lb);
    return kg === undefined ? undefined : weightObservation(kg);
  }, [v2, entered.lb, measurements.weight]);
  const liveHeight = useMemo(() => {
    if (!v2 || (entered.ft === undefined && entered.inches === undefined)) return measurements.height;
    const cm = enteredHeightCm(shownEntered.ft, shownEntered.inches);
    return cm === undefined ? undefined : heightObservation(cm);
  }, [v2, entered.ft, entered.inches, shownEntered.ft, shownEntered.inches, measurements.height]);
  const bmi = useMemo(() => describeBmi(liveWeight, liveHeight), [liveWeight, liveHeight]);

  /**
   * Leaving a box writes a new weight or height Observation, when the boxes
   * hold a usable value that isn't what the chart already says. One write per
   * measurement at a time: leaving the box again while one is in flight queues
   * one more commit, run with the boxes as they are then. A failed write keeps
   * what was typed and says so.
   */
  const measurementWrites = useRef(new Set<'weight' | 'height'>());
  const measurementRecommits = useRef(new Set<'weight' | 'height'>());
  /** The latest boxes and chart, for a commit that runs after a write returns. */
  const measurementState = useRef({ entered, shownEntered, measurements });
  measurementState.current = { entered, shownEntered, measurements };
  const commitMeasurement = useCallback(
    async (measurement: 'weight' | 'height'): Promise<void> => {
      if (!patient?.id) return;
      if (measurementWrites.current.has(measurement)) {
        measurementRecommits.current.add(measurement);
        return;
      }
      const { entered: held, shownEntered: shown, measurements: chart } = measurementState.current;
      const fields: (keyof EnteredMeasurements)[] = measurement === 'weight' ? ['lb'] : ['ft', 'inches'];
      if (!fields.some((field) => held[field] !== undefined)) return;
      const value = measurement === 'weight' ? enteredWeightKg(shown.lb) : enteredHeightCm(shown.ft, shown.inches);
      if (value === undefined) return;
      const current =
        measurement === 'weight' ? observationWeightToKg(chart.weight) : observationHeightToCm(chart.height);
      // Already the chart's value, e.g. what was just written: keep what was typed.
      if (current !== undefined && Math.abs(current - value) < 1e-9) return;
      // The boxes say what they were prefilled with: the chart's value at the
      // boxes' whole units, not a new measurement. Nothing is written, and the
      // boxes go back to following the chart.
      const prefill = enteredFromObservations(chart.weight, chart.height);
      if (fields.every((field) => shown[field].trim() === prefill[field])) {
        setEntered((typed) =>
          Object.fromEntries(
            Object.entries(typed).filter(([field]) => !fields.includes(field as keyof EnteredMeasurements))
          )
        );
        return;
      }
      const subject = createReference(patient);
      const draft = measurement === 'weight' ? weightObservation(value, subject) : heightObservation(value, subject);
      measurementWrites.current.add(measurement);
      setSavingMeasurement(true);
      try {
        const created = await medplum.createResource<Observation>({
          ...draft,
          effectiveDateTime: new Date().toISOString(),
        });
        // What was typed stays in the boxes; the write is now the latest measurement.
        measurementState.current = {
          ...measurementState.current,
          measurements: { ...measurementState.current.measurements, [measurement]: created },
        };
        setMeasurements((latest) => ({ ...latest, [measurement]: created }));
      } catch {
        // One failure, one message: no queued retry of the same value.
        measurementRecommits.current.delete(measurement);
        alert.error(`The ${measurement} could not be saved to the chart. What you typed is still here.`);
      } finally {
        measurementWrites.current.delete(measurement);
        setSavingMeasurement(measurementWrites.current.size > 0);
      }
      if (measurementRecommits.current.delete(measurement)) await commitMeasurement(measurement);
    },
    [patient, medplum]
  );

  // The insurance section (BAR-2919): signup's answers prefill it, and its
  // likely-eligibility line is computed here and recorded in the note.
  const signupInsurance = useSignupInsurance(patient, !readOnly);
  const patientState = coverageArea && coverageArea.status !== 'loading' ? coverageArea.state : undefined;
  const stateLoading = coverageArea?.status === 'loading';
  const insurerState = insurerListState(coverageArea);
  // Each field is prefilled at most once per note: the ids already filled are
  // kept on the draft, so a field the navigator clears stays clear, even after
  // a reload or when the other lookup lands later.
  // Only while the section applies: an untouched card for a patient it doesn't
  // apply to gets no write on open. Never the member ID or group number: those
  // rows read the Coverage itself, and the note must not hold them.
  const insuranceApplies = !insuranceSectionDisabledReason(answers, patient, applicabilityContext);
  useEffect(() => {
    if (readOnly || charted || !insuranceApplies) return;
    const insurer = signupInsurance?.insurer;
    const offered: [string, string | undefined][] = [
      // v2.0 reads the insurance sites from ELIGIBLE_LOCATIONS (BAR-3000), Indiana included.
      [
        PN_INSURANCE_TRAVEL_ID,
        v2 ? travelLabelForMarket(insuranceMarketForState(patientState)) : nearerTravelLocation(patientState),
      ],
      [PN_INSURANCE_SOURCE_ID, signupInsurance?.source],
      [PN_POLICYHOLDER_ID, signupInsurance?.policyholder],
      [PN_INSURER_ID, insurer?.name],
    ];
    if (offered.every(([, value]) => value === undefined)) return;
    updateAnswers((current) => {
      const prefilled = new Set(answerStrings(current.values[PN_INSURANCE_PREFILLED_ID]));
      const values = { ...current.values };
      for (const [id, value] of offered) {
        if (value === undefined || prefilled.has(id)) continue;
        prefilled.add(id);
        if (values[id] !== undefined) continue;
        values[id] = value;
        if (id === PN_INSURER_ID && insurer) {
          values[`${PN_INSURER_ID}Reference`] = insurer.reference;
          values[`${PN_INSURER_ID}Key`] = insurer.key;
        }
      }
      values[PN_INSURANCE_PREFILLED_ID] = [...prefilled];
      return { ...current, values };
    });
  }, [readOnly, charted, v2, insuranceApplies, signupInsurance, patientState, updateAnswers]);

  // Every input from the answers that apply, so nothing left on the draft from
  // a dropped answer (a stale insurer key, say) feeds the criteria.
  const insurerName = answerString(applicableAnswers.values[PN_INSURER_ID]);
  const insurerKey = answerString(applicableAnswers.values[`${PN_INSURER_ID}Key`]);
  const insuranceEligibility = useMemo(
    () =>
      (v2 ? pnCardV2InsuranceEligibility : likelyInsuranceEligibility)({
        bmi: bmi?.exact,
        conditions: readWeightRelatedConditions(applicableAnswers.values),
        source: answerString(applicableAnswers.values[PN_INSURANCE_SOURCE_ID]),
        insurer: insurerName ? { name: insurerName, key: insurerKey } : undefined,
        // Unknown for every insurer, so an eligible line ends "Network unsure":
        // there is no network source yet. bariendo-be's `INSURANCE_NETWORK`
        // map (bariendo-be #1163) will be read through the `coverage-lookup`
        // bot, beside `coverageArea`, and passed here. Never a list in Orbit.
        network: null,
      }),
    [v2, bmi, applicableAnswers.values, insurerName, insurerKey]
  );
  // Recorded, so the saved note keeps what the call concluded and later rows
  // (talking points, the verification button) can key off it. Not before the
  // measurements are read: a reopened draft would lose its line meanwhile.
  // A charted note keeps the line it was saved with until the navigator edits it.
  // A weight or height typed on a charted note is an edit too.
  const eligibilityAsRecorded = readOnly || (charted && !editedSinceOpen && Object.keys(entered).length === 0);
  useEffect(() => {
    if (eligibilityAsRecorded || !measurementsLoaded) return;
    updateAnswers((current) => ({
      ...current,
      values: {
        ...current.values,
        [PN_INSURANCE_ELIGIBILITY_ID]: insuranceEligibility?.label,
        // `conditional` only on v2.0 (BAR-2998): neither yes nor no, so neither
        // the approval rows nor the self-pay line follow until a condition is marked.
        [`${PN_INSURANCE_ELIGIBILITY_ID}Eligible`]: insuranceEligibility
          ? insuranceEligibility.eligible
            ? 'yes'
            : v2 && insuranceEligibility.conditional
              ? 'conditional'
              : 'no'
          : undefined,
      },
    }));
  }, [eligibilityAsRecorded, measurementsLoaded, insuranceEligibility, v2, updateAnswers]);

  // The pinned key-facts block: what the chart said going in, read the same way
  // as the Intake and billing panel. Never this card's live answers.
  // v2.0 has no key facts: the Track row is its only fact row.
  const preConsultation = usePreConsultationFacts(patient, !readOnly && !v2);
  const facts = useMemo((): Record<PacFactField, { value?: string; pending: boolean; missing?: string }> => {
    const age = ageInYears(patient?.birthDate, new Date());
    return {
      procedure: { value: preConsultation.procedure, pending: preConsultation.loading },
      location: { value: preConsultation.location, pending: preConsultation.loading },
      track: { value: preConsultation.track, pending: preConsultation.loading, missing: TRACK_NOT_RECORDED },
      bmi: { value: bmi?.value, pending: Boolean(patient?.id) && !measurementsLoaded },
      age: { value: age === undefined ? undefined : String(age), pending: false },
    };
  }, [
    preConsultation.procedure,
    preConsultation.location,
    preConsultation.track,
    preConsultation.loading,
    patient?.id,
    patient?.birthDate,
    bmi,
    measurementsLoaded,
  ]);

  const weightLossEstimate = useMemo(() => {
    const procedure = typeof answers.values.procedure === 'string' ? answers.values.procedure : undefined;
    return calculateWeightLossEstimate(liveWeight, procedure);
  }, [answers.values.procedure, liveWeight]);

  /**
   * Choosing a site records its name (what the navigator says, and what the
   * Intake & billing panel reads as the market) and its reference (what the
   * board is scoped to). Changing site drops any date agreed at the previous one.
   */
  const handleSelectLocation = useCallback(
    (name: string | null) => {
      const option = locations.options.find((entry) => entry.name === name);
      updateAnswers((current) => {
        const { procedureDate: _dropped, ...rest } = current;
        return {
          ...rest,
          values: {
            ...current.values,
            [LOCATION_ID]: option?.name,
            [LOCATION_REFERENCE_ID]: option?.reference,
            [PROCEDURE_DATE_ID]: undefined,
          },
        };
      });
    },
    [locations.options, updateAnswers]
  );

  // The physicians who actually have blocks at the site — deduplicated from the
  // board, in the order their first block falls.
  const sitePhysicians = useMemo(
    () => [...new Set(dates.options.map((option) => option.physicianName).filter(Boolean))] as string[],
    [dates.options]
  );

  const declined = answers.values[PROCEDURE_DATE_ID] === NOT_READY_TO_COMMIT;
  const saveDisabledReason = pnConsultationSaveBlocker(answers);

  /** The location select's choices: every procedure Location, or on v2.0 only the self-pay centres (BAR-3000). */
  const locationNames = useCallback(
    (widget: Extract<PacWidget, { kind: 'locationSelect' }>): string[] => {
      const names = locations.options.map((option) => option.name);
      if (!widget.eligibleOnly) return names;
      const current = answerString(answers.values[widget.id]);
      const eligible = names.filter((name) => (ELIGIBLE_LOCATIONS.selfPay as readonly string[]).includes(name));
      // A stored site no longer offered still needs an entry, or the select shows empty.
      return current && !eligible.includes(current) ? [...eligible, current] : eligible;
    },
    [locations.options, answers.values]
  );

  /** The widget kinds this script has that the shared spine does not know. */
  const renderCardWidget = useCallback(
    (widget: PacWidget) => {
      if (widget.kind === 'locationSelect') {
        const current = typeof answers.values[widget.id] === 'string' ? (answers.values[widget.id] as string) : null;
        return (
          <Row label={widget.label} say={widget.say}>
            <Select
              size="xs"
              w={v2 ? '100%' : '56%'}
              clearable
              searchable
              disabled={readOnly || locations.loading}
              placeholder={locations.loading ? 'Loading locations…' : undefined}
              data={locationNames(widget)}
              value={current}
              onChange={handleSelectLocation}
            />
          </Row>
        );
      }

      if (widget.kind === 'switchTrackLink') {
        if (readOnly || widget.to === cardTrack) return null;
        return (
          <QsaRowLink onClick={() => handleTrackChange(widget.to)} testId={`pn-switch-to-${widget.to}`}>
            Switch to {PN_CARD_TRACK_LABELS[widget.to]}
          </QsaRowLink>
        );
      }

      if (widget.kind === 'employerLocationSelect') {
        const current = answerString(shownAnswers.values[widget.id]) ?? null;
        const markets = employerMarkets(employerAdministrator);
        return (
          <Row label={widget.label} say={widget.say}>
            {markets.length > 0 ? (
              <Select
                size="xs"
                w="100%"
                clearable
                disabled={readOnly}
                aria-label={widget.label}
                data={
                  current && !(markets as readonly string[]).includes(current) ? [...markets, current] : [...markets]
                }
                value={current}
                onChange={(value) => setValue(widget.id, value ?? undefined)}
              />
            ) : (
              <Text size="sm" c="dimmed">
                Confirm the employer benefit first
              </Text>
            )}
          </Row>
        );
      }

      if (widget.kind === 'procedureDateGrid') {
        const grid = !locationReference ? (
          <Text size="sm" c="dimmed" py={4}>
            Confirm the location first — the dates come from that site&rsquo;s board.
          </Text>
        ) : (
          <ProcedureDateGrid
            options={dates.options}
            loading={dates.loading}
            error={dates.error}
            selected={answers.procedureDate}
            declined={declined}
            onSelect={handleSelectDate}
            onDecline={handleDecline}
            // "Not ready to commit yet" is one of the timing chips below; asked once.
            allowDecline={false}
            disabled={readOnly}
          />
        );
        // v2.0 rows are Question · Answer; the shipped card draws the grid on its own.
        return v2 ? <Row label="Date">{grid}</Row> : grid;
      }

      if (widget.kind === 'employerSearch') {
        return (
          <EmployerSearchWidget
            widget={widget}
            answers={shownAnswers}
            updateAnswers={updateAnswers}
            patient={patient}
            readOnly={readOnly}
            // The employer on file is the patient's own, never a family member's.
            prefillFromFile={widget.id === PN_EMPLOYER_ID}
            allowPrefill={!charted}
          />
        );
      }

      // The coverage sections' next step. Reads the answers that apply.
      if (widget.kind === 'verificationTask') {
        const task =
          widget.coverage === 'employer' ? (
            <EmployerVerificationTask
              widget={widget}
              answers={applicableAnswers}
              onDone={recordCoverageStep}
              patient={patient}
              readOnly={readOnly}
              financing={v2}
            />
          ) : (
            <InsuranceVerificationTask
              widget={widget}
              answers={applicableAnswers}
              onDone={recordCoverageStep}
              patient={patient}
              readOnly={readOnly}
              withHeldCardDetails={insuranceCard.writeHeld}
              financing={v2}
            />
          );
        return v2 ? <Row label="Verification">{task}</Row> : task;
      }

      if (widget.kind === 'insurerSelect') {
        return (
          <InsurerSelectWidget
            widget={widget}
            answers={shownAnswers}
            updateAnswers={updateAnswers}
            state={insurerState}
            stateLoading={stateLoading}
            // Nothing from signup into a charted note reopened (BAR-2996).
            signupName={charted ? undefined : signupInsurance?.insurerName}
            readOnly={readOnly}
          />
        );
      }

      // Live while the note is written; a saved note shows what it recorded.
      if (widget.kind === 'insuranceEligibility') {
        const label = eligibilityAsRecorded
          ? answerString(shownAnswers.values[widget.id])
          : insuranceEligibility?.label;
        const eligible = eligibilityAsRecorded
          ? shownAnswers.values[`${widget.id}Eligible`] === 'yes'
          : Boolean(insuranceEligibility?.eligible);
        const conditional = eligibilityAsRecorded
          ? shownAnswers.values[`${widget.id}Eligible`] === 'conditional'
          : Boolean(v2 && insuranceEligibility?.conditional);
        const color = eligible ? ORBIT_SETTLED : ORBIT_NEEDS_ACTION;
        return (
          <Row label={widget.label}>
            {label ? (
              <Group gap={4} wrap="nowrap" justify="flex-end">
                {eligible ? (
                  <IconCheck size={14} color={color} />
                ) : conditional ? (
                  <PendingMark />
                ) : (
                  <IconX size={14} color={color} />
                )}
                <Text size="sm" fw={500} c={color} ta="right">
                  {label}
                </Text>
              </Group>
            ) : (
              <Text size="sm" c="dimmed" ta="right">
                {bmi || eligibilityAsRecorded ? '—' : 'Weight or height not on file'}
              </Text>
            )}
          </Row>
        );
      }

      if (widget.kind === 'policyholderField') {
        const relationship = policyholderRelationship(shownAnswers.values[PN_POLICYHOLDER_ID]);
        if (!relationship) return null;
        return (
          <PolicyholderRow
            label={widget.label}
            say={widget.say}
            patient={patient}
            relationship={relationship}
            editable={!readOnly && mayEditInsuranceCard}
          />
        );
      }

      if (widget.kind === 'fact') {
        const { value, pending, missing = 'Not on file' } = facts[widget.field];
        return (
          <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm" py={4}>
            <Text size="sm" c="dimmed">
              {widget.label}
            </Text>
            <Text size="sm" fw={value ? 500 : 400} c={value ? undefined : 'dimmed'} ta="right">
              {value ?? (pending ? 'Loading…' : missing)}
            </Text>
          </Group>
        );
      }

      if (widget.kind === 'profileField') {
        return <PatientProfileFieldRow widget={widget} patient={patient} onSave={saveProfileField} />;
      }

      if (widget.kind === 'insuranceCardField') {
        return (
          <InsuranceCardFieldRow widget={widget} card={insuranceCard} editable={!readOnly && mayEditInsuranceCard} />
        );
      }

      if (widget.kind !== 'computed') return null;

      switch (widget.field) {
        case 'employerBenefit': {
          const result = describeEmployerBenefit(shownAnswers);
          const color = result?.covered ? ORBIT_SETTLED : ORBIT_NEEDS_ACTION;
          return (
            <Row label={widget.label}>
              {result ? (
                <Group gap={4} wrap="nowrap" justify="flex-end">
                  {result.covered ? <IconCheck size={14} color={color} /> : <IconX size={14} color={color} />}
                  <Text size="sm" fw={500} c={color}>
                    {result.label}
                  </Text>
                </Group>
              ) : (
                <Text size="sm" c="dimmed" ta="right">
                  —
                </Text>
              )}
            </Row>
          );
        }
        case 'bmi':
          if (v2) {
            return (
              <Row label={widget.label} say={widget.say}>
                <WeightHeightInputs
                  values={shownEntered}
                  onChange={(field, value) => setEntered((held) => ({ ...held, [field]: value }))}
                  onCommit={(measurement) => void commitMeasurement(measurement)}
                  bmi={bmi}
                  saving={savingMeasurement}
                  // Not before the chart is read: a typed value would be compared with nothing.
                  disabled={readOnly || !patient?.id || !measurementsLoaded}
                />
              </Row>
            );
          }
          return (
            <Row label={widget.label} say={widget.say}>
              {bmi ? (
                <Text size="sm" fw={500} ta="right">
                  {formatWeightHeightBmi(bmi)}
                </Text>
              ) : (
                <Text size="sm" c="dimmed" ta="right">
                  Weight or height not on file
                </Text>
              )}
            </Row>
          );
        case 'weightLossEstimate':
          return <WeightLossEstimateRow label={widget.label} say={widget.say} estimate={weightLossEstimate} />;
        case 'sitePhysicians': {
          if (!locationReference) return null;
          const value = dates.loading
            ? 'Reading the schedule…'
            : sitePhysicians.length > 0
              ? sitePhysicians.join(', ')
              : 'No procedure blocks at this location in the next 90 days';
          return (
            <Row label={widget.label}>
              <Text
                size="sm"
                fw={sitePhysicians.length > 0 ? 500 : 400}
                c={sitePhysicians.length > 0 ? undefined : 'dimmed'}
                ta="right"
              >
                {value}
              </Text>
            </Row>
          );
        }
        case 'cherryApplication': {
          // Cherry has approved the patient: there is nothing left to apply for (BAR-2923).
          if (cherryStatus?.state === 'approved') {
            return (
              <Row label={widget.label} say={widget.say}>
                <Group gap={4} wrap="nowrap">
                  <IconCheck size={14} color={ORBIT_SETTLED} />
                  <Text size="sm" fw={500} c={ORBIT_SETTLED}>
                    {describeCherryStatus(cherryStatus)?.label}
                  </Text>
                </Group>
              </Row>
            );
          }
          const sent = answerString(shownAnswers.values[PN_CHERRY_APPLICATION_SENT_ID]);
          const send = async () => {
            if (!(await handleAction('sendCherryLink'))) return;
            updateAnswers((current) => ({
              ...current,
              values: { ...current.values, [PN_CHERRY_APPLICATION_SENT_ID]: new Date().toISOString() },
            }));
            void saveNow();
          };
          return (
            <Row label={widget.label} say={widget.say}>
              {sent ? (
                <Group gap={8} wrap="nowrap">
                  <Group gap={4} wrap="nowrap">
                    <IconCheck size={14} color={ORBIT_SETTLED} />
                    <Text size="sm" fw={500} c={ORBIT_SETTLED}>
                      Application link sent
                    </Text>
                  </Group>
                  <Anchor
                    component="button"
                    type="button"
                    size="sm"
                    c="green.9"
                    disabled={readOnly || pendingAction === 'sendCherryLink'}
                    onClick={() => void send()}
                    data-testid="pn-cherry-resend"
                  >
                    Resend
                  </Anchor>
                </Group>
              ) : (
                <Button
                  size="compact-sm"
                  color="green.9"
                  disabled={readOnly}
                  loading={pendingAction === 'sendCherryLink'}
                  onClick={() => void send()}
                  data-testid="pn-cherry-send"
                >
                  Send Cherry application
                </Button>
              )}
            </Row>
          );
        }
        case 'cherryAccount':
          return (
            <CherryAccountRow
              label={widget.label}
              status={cherryStatus}
              loading={cherryChecking}
              onSend={() => void handleAction('sendCherryLink')}
              sending={pendingAction === 'sendCherryLink'}
              disabled={readOnly}
            />
          );
        default:
          return null;
      }
    },
    [
      locations.loading,
      locationReference,
      handleSelectLocation,
      dates,
      declined,
      handleSelectDate,
      handleDecline,
      readOnly,
      charted,
      cardTrack,
      handleTrackChange,
      eligibilityAsRecorded,
      locationNames,
      employerAdministrator,
      setValue,
      saveNow,
      shownEntered,
      commitMeasurement,
      savingMeasurement,
      measurementsLoaded,
      v2,
      cherryStatus,
      cherryChecking,
      handleAction,
      pendingAction,
      bmi,
      weightLossEstimate,
      sitePhysicians,
      facts,
      answers,
      updateAnswers,
      patient,
      saveProfileField,
      shownAnswers,
      applicableAnswers,
      recordCoverageStep,
      stateLoading,
      insurerState,
      signupInsurance,
      insuranceEligibility,
      insuranceCard,
      mayEditInsuranceCard,
    ]
  );

  if (v2Script) {
    return (
      <PnConsultationV2Body<PnConsultationAnswers>
        script={v2Script}
        answers={shownAnswers}
        context={context}
        setValue={setValue}
        onAction={handleScriptAction}
        pendingAction={pendingAction}
        unavailableActions={unavailableActions}
        renderCardWidget={renderCardWidget}
        track={cardTrack}
        onTrackChange={handleTrackChange}
        hideScript={hideScript}
        readOnly={readOnly}
        isSaving={isSaving || switchingTrack}
        saveError={saveError}
        saveBlocked={Boolean(saveDisabledReason)}
        charted={charted}
        trackSwitch={switchOnSave}
        onSave={(acknowledged) => void saveV2(acknowledged)}
        onOpenSectionsChange={setOpenSections}
      />
    );
  }

  return (
    <PatientAccessCallCard<PnConsultationAnswers>
      script={script}
      answers={shownAnswers}
      patient={patient}
      context={context}
      setValue={setValue}
      summarizeBeat={summarizePnConsultationBeat}
      onAction={handleScriptAction}
      pendingAction={pendingAction}
      renderCardWidget={renderCardWidget}
      // Most beats here are read, not answered; a tick that can never light is noise.
      showBeatProgress={false}
      readOnly={readOnly}
      isSaving={isSaving}
      saveError={saveError}
      saveDisabledReason={saveDisabledReason}
      onSave={() => {
        heldAtSaveRef.current = insuranceCard.pending;
        void saveToChart();
      }}
      onOpenBeatChange={setOpenBeat}
    />
  );
}
